import { useState, useEffect } from 'react';

/**
 * Hook para detectar y manejar la instalación de PWA
 * Detecta si la app ya está instalada y maneja el prompt de instalación
 */
export const usePWAInstall = () => {
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detectar si es iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Detectar si es Android
    const android = /Android/.test(navigator.userAgent);
    setIsAndroid(android);

    // Detectar si la app está en modo standalone (ya instalada)
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      window.navigator.standalone ||
                      document.referrer.includes('android-app://');
    setIsStandalone(standalone);
    setIsInstalled(standalone);

    // Detectar el evento beforeinstallprompt (Chrome, Edge, etc.)
    const handleBeforeInstallPrompt = (e) => {
      // Prevenir el prompt automático
      e.preventDefault();
      // Guardar el evento para usarlo más tarde
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Detectar cuando la app se instala
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Si ya está instalada, no mostrar el banner
    if (standalone) {
      setIsInstallable(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Función para mostrar el prompt de instalación
  const promptInstall = async () => {
    if (deferredPrompt) {
      // Mostrar el prompt de instalación
      deferredPrompt.prompt();
      
      // Esperar a que el usuario responda
      const { outcome } = await deferredPrompt.userChoice;
      
      // Limpiar el prompt guardado
      setDeferredPrompt(null);
      setIsInstallable(false);

      if (outcome === 'accepted') {
        setIsInstalled(true);
        return { success: true, message: 'App instalada exitosamente' };
      } else {
        return { success: false, message: 'Instalación cancelada' };
      }
    }
    
    return { success: false, message: 'No se puede instalar en este dispositivo' };
  };

  return {
    isInstalled,
    isInstallable,
    isIOS,
    isAndroid,
    isStandalone,
    promptInstall,
    canInstall: !!deferredPrompt || isIOS || isAndroid
  };
};

export default usePWAInstall;
